/**
 * 日期选择器 Web Component
 *
 * 单日期: <ro-date-picker label="Date of Birth" value="1999-09-29"></ro-date-picker>
 * 日期范围: <ro-date-picker mode="range" label="Select Period" value="2024-01-07,2024-01-29"></ro-date-picker>
 * 普通 placeholder（无上浮）: <ro-date-picker floating-label="false" label="选择日期"></ro-date-picker>
 * 尺寸: size="large" | "medium"（默认 48px）| "small"
 * value 格式: single 为 "YYYY-MM-DD"，range 为 "start,end"
 * 单日期且无 value 时: data-default-open-date="YYYY-MM-DD" 指定打开日历时初始展示的月份（也支持 today）
 */

(function() {
  'use strict';

  // 避免重复定义
  if (customElements.get('ro-date-picker')) {
    return;
  }

  /**
   * 日期选择器管理器
   * 全局单例，用于管理所有日期选择器实例
   */
  window.DatePickerManager = window.DatePickerManager || {
    instances: {},
    
    getValue: function(id) {
      const picker = this.instances[id];
      return picker ? picker.value : null;
    },

    setValue: function(id, date) {
      const picker = this.instances[id];
      if (picker) {
        picker.value = date;
      }
    },

    getAllValues: function() {
      const values = {};
      for (const id in this.instances) {
        values[id] = this.instances[id].value;
      }
      return values;
    },

    register: function(id, instance) {
      this.instances[id] = instance;
    },

    unregister: function(id) {
      delete this.instances[id];
    }
  };

  /**
   * 日期选择器组件样式
   */
  const styles = `
    :host {
      display: block;
      position: relative;
      width: 100%;
    }

    * {
      box-sizing: border-box;
    }

    /* ==================== 输入框样式 ==================== */
    /* 默认 size：medium（未写属性时由 connectedCallback 设为 medium）→ 48px；large / small 见 :host([size=...]) */
    .date-picker__field {
      display: flex;
      align-items: stretch;
      gap: 12px;
      box-sizing: border-box;
      height: 48px;
      min-height: 48px;
      max-height: 48px;
      padding: 8px 16px;
      background: var(--bg-white-color);
      border: 1px solid var(--border-color);
      border-radius: var(--ro-field-border-radius, 4px);
      cursor: pointer;
      transition: border-color 0.2s ease;
    }

    :host([size="large"]) .date-picker__field {
      height: 56px;
      min-height: 56px;
      max-height: 56px;
      padding: 8px 16px;
    }

    :host([size="medium"]) .date-picker__field {
      height: 48px;
      min-height: 48px;
      max-height: 48px;
      padding: 8px 16px;
    }

    :host([size="small"]) .date-picker__field {
      height: 44px;
      min-height: 44px;
      max-height: 44px;
      padding: 6px 16px;
    }

    .date-picker__field:hover {
      border-color: var(--main-color);
    }

    .date-picker__field--focus {
      border-color: var(--main-color);
    }

    .date-picker__field--error {
      border-color: var(--error-color, #d93840);
    }

    .date-picker__field--disabled {
      cursor: not-allowed;
      opacity: 0.5;
      background: var(--bg-color, #f6f6f6);
      pointer-events: none;
    }

    .date-picker__field--disabled:hover {
      border-color: var(--border-color);
    }

    .date-picker__error-message {
      display: none;
      margin-top: 8px;
      padding: 0 16px;
      color: var(--error-color, #d93840);
      font-size: 12px;
      line-height: 16px;
    }

    .date-picker__error-message--visible {
      display: block;
    }

    @media (max-width: 1183px) {
      .date-picker__error-message {
        padding: 0 12px;
      }
    }

    /* 输入区：在固定高度 field 内撑满剩余宽度，inner 块垂直居中（无高度抖动） */
    .date-picker__content {
      flex: 1;
      position: relative;
      min-width: 0;
      min-height: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: stretch;
      align-self: stretch;
    }

    /* label + value 纵向栈，类型一有值时自上而下排列 */
    .date-picker__inner {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: stretch;
      gap: 2px;
      width: 100%;
      min-width: 0;
      min-height: 0;
    }

    /* ========== 类型一 Floating label（默认）：content 高度随 field 固定，选日前后不变化 ========== */
    :host(:not([floating-label="false"])) .date-picker__label {
      position: static;
      transform: none;
      left: auto;
      right: auto;
      flex-shrink: 0;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: var(--second-color);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: font-size 0.28s cubic-bezier(0.4, 0, 0.2, 1),
        line-height 0.28s cubic-bezier(0.4, 0, 0.2, 1),
        font-weight 0.28s cubic-bezier(0.4, 0, 0.2, 1),
        color 0.2s ease;
    }

    :host(:not([floating-label="false"])) .date-picker__value {
      position: static;
      flex-shrink: 0;
      font-size: 16px;
      font-weight: 400;
      line-height: 20px;
      color: var(--main-color);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: opacity 0.28s cubic-bezier(0.4, 0, 0.2, 1),
        max-height 0.28s cubic-bezier(0.4, 0, 0.2, 1),
        transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
        margin 0.28s ease,
        line-height 0.28s cubic-bezier(0.4, 0, 0.2, 1),
        font-size 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* medium / small：Floating 下行高（字号不变） */
    :host(:not([floating-label="false"]):not([size="large"])) .date-picker__value {
      line-height: 16px;
    }

    /* 未选：仅 placeholder 可视，日期行折叠，inner 总高度≈一行，由 content flex 整体垂直居中 */
    :host(:not([floating-label="false"])) .date-picker__content:not(.date-picker__content--has-value) .date-picker__value {
      max-height: 0;
      opacity: 0;
      margin: 0;
      padding: 0;
      overflow: hidden;
      line-height: 0;
      transform: translateY(4px);
    }

    /* 已选：label 在上、value 在下，两行 + gap 作为整体仍在 content 内垂直居中 */
    :host(:not([floating-label="false"])) .date-picker__content--has-value .date-picker__label {
      font-size: 12px;
      font-weight: 500;
      line-height: 16px;
    }

    :host(:not([floating-label="false"])) .date-picker__content--has-value .date-picker__value {
      max-height: 32px;
      opacity: 1;
      line-height: 20px;
      transform: translateY(0);
    }

    /* medium / small：Floating label 行高 14px（字号仍为 14px / 12px） */
    :host(:not([floating-label="false"]):not([size="large"])) .date-picker__label,
    :host(:not([floating-label="false"]):not([size="large"])) .date-picker__content--has-value .date-picker__label {
      line-height: 14px;
    }

    /* medium / small（及默认 size="medium"）：有值时日期为 14px、行高 16px；large 保持原样 */
    :host(:not([size="large"])) .date-picker__content--has-value .date-picker__value {
      font-size: 14px;
      line-height: 16px;
    }

    /* ========== 类型二：普通 placeholder（floating-label="false"） ========== */
    :host([floating-label="false"]) .date-picker__inner {
      position: relative;
      height: 100%;
      gap: 0;
    }

    :host([floating-label="false"]) .date-picker__label {
      position: absolute;
      left: 0;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: var(--second-color);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: opacity 0.2s ease, visibility 0.2s ease;
    }

    :host([floating-label="false"]) .date-picker__value {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      height: 100%;
      display: flex;
      align-items: center;
      font-size: 16px;
      font-weight: 400;
      line-height: 20px;
      color: var(--main-color);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transform: none;
      transition: opacity 0.2s ease;
    }

    /* medium / small：普通 placeholder 下行高（字号不变） */
    :host([floating-label="false"]:not([size="large"])) .date-picker__label {
      line-height: 14px;
    }

    :host([floating-label="false"]:not([size="large"])) .date-picker__value {
      line-height: 16px;
    }

    :host([floating-label="false"]) .date-picker__content:not(.date-picker__content--has-value) .date-picker__value {
      opacity: 0;
      pointer-events: none;
    }

    :host([floating-label="false"]) .date-picker__content--has-value .date-picker__label {
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    /* 有值：与类型一行高一致，整行（含全角 ～）在输入区内垂直居中；不用 flex 避免与半角 ~ 行框对齐差异 */
    :host([floating-label="false"]) .date-picker__content--has-value .date-picker__value {
      opacity: 1;
      display: block;
      top: 50%;
      bottom: auto;
      height: auto;
      transform: translateY(-50%);
    }

    .date-picker__clear-btn {
      display: none;
      align-items: center;
      justify-content: center;
      align-self: center;
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      padding: 0;
      background: transparent;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      color: var(--second-color);
      transition: color 0.2s ease, background-color 0.2s ease;
    }

    .date-picker__field--has-value:hover .date-picker__clear-btn {
      display: flex;
    }

    .date-picker__clear-btn:hover {
      color: var(--main-color);
      background: var(--bg-color);
    }

    .date-picker__icon {
      flex-shrink: 0;
      align-self: center;
      width: 24px;
      height: 24px;
      color: var(--main-color);
    }

    /* ==================== PC端下拉日历 ==================== */
    /* 高于页面 header（z-index: 10000），避免面板被遮挡 */
    .date-picker__dropdown {
      display: none;
      position: fixed;
      z-index: 10050;
      width: 320px;
      background: var(--bg-white-color);
      border-radius: 4px;
      box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.15);
      transform: translateY(-8px);
      transition: transform 0.2s ease;
    }

    .date-picker__dropdown--open {
      display: block;
      transform: translateY(0);
    }

    /* ==================== 日历头部 ==================== */
    .date-picker__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px;
      border-bottom: 1px solid var(--border-color);
    }

    .date-picker__nav-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      background: transparent;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      color: var(--main-color);
      transition: background-color 0.2s ease;
    }

    .date-picker__nav-btn:hover {
      background: var(--bg-color);
    }

    .date-picker__nav-btn:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .date-picker__current {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .date-picker__month-year {
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      color: var(--main-color);
      cursor: pointer;
      padding: 0px 8px;
      transition: background-color 0.2s ease;
    }

    .date-picker__month-year:hover {
      background: var(--bg-color);
    }

    /* ==================== 日历主体 ==================== */
    .date-picker__body {
      padding: 12px 16px;
    }

    .date-picker__weekdays {
      display: grid;
      grid-template-columns: repeat(7, 33px);
      justify-content: space-between;
      padding: 0 0 12px;
    }

    .date-picker__weekday {
      font-size: 12px;
      font-weight: 500;
      line-height: 16px;
      color: var(--second-color);
      text-align: center;
    }

    .date-picker__days {
      display: grid;
      grid-template-columns: repeat(7, 33px);
      justify-content: space-between;
      row-gap: 12px;
      grid-auto-rows: 33px;
    }

    .date-picker__day {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 33px;
      height: 33px;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: var(--main-color);
      background: transparent;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;
    }

    .date-picker__day:hover:not(:disabled):not(.date-picker__day--selected):not(.date-picker__day--in-range) {
      background: var(--bg-color);
    }

    .date-picker__day--other-month {
      color: var(--second-color);
      opacity: 0.5;
    }

    .date-picker__day--today {
      font-weight: 600;
      color: var(--brand-color);
    }

    .date-picker__day--selected {
      background: var(--main-color);
      color: var(--bg-white-color);
      font-weight: 500;
    }

    .date-picker__day:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    /* 日期范围：连续 pill 背景（由 .date-picker__range-bar 实现），日期格无背景 */
    .date-picker__day--in-range {
      color: var(--main-color);
    }

    .date-picker__day--range-start.date-picker__day--selected,
    .date-picker__day--range-end.date-picker__day--selected {
      background: var(--main-color);
      color: var(--bg-white-color);
    }

    /* 日期范围：周行 + 连续 pill 条 */
    .date-picker__days--range {
      display: flex;
      flex-direction: column;
      row-gap: 12px;
    }

    .date-picker__week-row {
      display: grid;
      grid-template-columns: repeat(7, 33px);
      justify-content: space-between;
      position: relative;
      min-height: 33px;
    }

    .date-picker__range-bar {
      position: absolute;
      top: 0;
      height: 33px;
      background: var(--background-secondary, #f0f0f0);
      border-radius: 16px;
      pointer-events: none;
      z-index: 0;
    }

    .date-picker__week-row .date-picker__day {
      position: relative;
      z-index: 1;
    }

    /* 日期范围 footer：Start / End 摘要 */
    .date-picker__range-footer {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
      border-top: 1px solid var(--border-color);
      font-size: 14px;
      line-height: 20px;
    }

    .date-picker__range-footer .date-picker__range-label {
      color: var(--second-color);
      margin-right: 4px;
    }

    .date-picker__range-footer .date-picker__range-value {
      color: var(--main-color);
    }

    .date-picker__range-sep {
      color: var(--second-color);
      font-size: 12px;
    }

    .date-picker__range-footer {
      display: none;
    }

    .date-picker__range-footer--visible {
      display: flex;
    }

    /* ==================== 年月选择器 ==================== */
    .date-picker__selector {
      display: none;
      padding: 16px;
    }

    .date-picker__selector--open {
      display: block;
    }

    .date-picker__selector-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    .date-picker__selector-title {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: var(--main-color);
    }

    .date-picker__selector-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      max-height: 240px;
      overflow-y: auto;
    }

    .date-picker__selector-item {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 12px 8px;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: var(--main-color);
      background: transparent;
      border: 1px solid transparent;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .date-picker__selector-item:hover {
      background: var(--bg-color);
    }

    .date-picker__selector-item--selected {
      background: var(--main-color);
      color: var(--bg-white-color);
    }

    .date-picker__selector-item--selected:hover {
      background: var(--main-color);
    }

    .date-picker__selector-item:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    /* ==================== 移动端弹窗样式 ==================== */
    /* 高于页面 header（z-index: 10000） */
    .date-picker__modal {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10050;
      background: rgba(0, 0, 0, 0.5); /* 遮罩层，vars.css 无对应变量保留原值 */
      align-items: center;
      justify-content: center;
      padding: 24px;
    }

    .date-picker__modal--open {
      display: flex;
    }

    .date-picker__modal-content {
      position: relative;
      width: 100%;
      max-width: 340px;
      max-height: 90vh;
      background: var(--bg-white-color);
      border-radius: 12px;
      overflow: hidden;
      transform: scale(0.95);
      transition: transform 0.3s ease;
    }

    .date-picker__modal--open .date-picker__modal-content {
      transform: scale(1);
    }

    .date-picker__modal-body {
      padding: 0;
      overflow-y: auto;
      max-height: calc(90vh - 88px);
    }

    .date-picker__modal .date-picker__header {
      padding: 16px;
      border-bottom: 0.5px solid var(--divider, #e1e1e1);
    }

    .date-picker__modal .date-picker__body {
      padding: 12px 16px;
    }

    .date-picker__modal-footer {
      display: flex;
      gap: 12px;
      padding: 16px 20px;
      border-top: 1px solid var(--border-color);
    }

    .date-picker__confirm-btn {
      flex: 1;
      padding: 12px 24px;
      font-family: inherit;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      color: var(--bg-white-color);
      background: var(--main-color);
      border: none;
      border-radius: 24px;
      cursor: pointer;
      transition: opacity 0.2s ease;
    }

    .date-picker__confirm-btn:hover {
      opacity: 0.8;
    }

    .date-picker__cancel-btn {
      flex: 1;
      padding: 12px 24px;
      font-family: inherit;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      color: var(--main-color);
      background: transparent;
      border: 1px solid var(--border-color);
      border-radius: 24px;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .date-picker__cancel-btn:hover {
      border-color: var(--main-color);
    }

    /* ==================== 响应式 ==================== */
    @media (max-width: 1183px) {
      .date-picker__dropdown {
        display: none !important;
      }
    }

    @media (min-width: 1184px) {
      .date-picker__modal {
        display: none !important;
      }
    }
  `;

  /**
   * PC 端日期面板的横向宽度（与 .date-picker__dropdown 的 width 一致）
   * 打开前下拉层为 display:none，offsetWidth 不可靠，定位计算使用此常量
   */
  const PC_DROPDOWN_PANEL_WIDTH = 320;

  /**
   * 输入框内日期范围展示分隔符（全角 U+FF5E），与 Floating（类型一）设计一致；避免与半角 ~ 混用导致视觉不一
   */
  const RANGE_INPUT_DISPLAY_SEPARATOR = ' \uFF5E ';

  /**
   * 左右箭头 SVG（与 assets/ro_datepicker_arrow_left.svg、ro_datepicker_arrow_right.svg 一致，stroke 用 currentColor 以继承文字色）
   */
  const ICON_ARROW_LEFT = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 17L9 12L14 7" stroke="currentColor" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/></svg>`;
  const ICON_ARROW_RIGHT = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 17L15 12L10 7" stroke="currentColor" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/></svg>`;

  /**
   * 日期选择器英文 fallback。站点语言由 window.RoComponentsLocale.datePicker 覆盖，
   * 避免在各站点维护分叉的组件脚本。
   */
  const DATE_PICKER_I18N_DEFAULTS = {
    label_default: 'Select Date',
    clear_aria_label: 'Clear date',
    prev_month_aria_label: 'Previous month',
    next_month_aria_label: 'Next month',
    year_selector_title: 'Select Year',
    month_selector_title: 'Select Month',
    back_aria_label: 'Back',
    cancel_button: 'Cancel',
    confirm_button: 'Confirm',
    weekday_sun: 'Sun',
    weekday_mon: 'Mon',
    weekday_tue: 'Tue',
    weekday_wed: 'Wed',
    weekday_thu: 'Thu',
    weekday_fri: 'Fri',
    weekday_sat: 'Sat',
    month_january: 'January',
    month_february: 'February',
    month_march: 'March',
    month_april: 'April',
    month_may: 'May',
    month_june: 'June',
    month_july: 'July',
    month_august: 'August',
    month_september: 'September',
    month_october: 'October',
    month_november: 'November',
    month_december: 'December',
    month_short_jan: 'Jan',
    month_short_feb: 'Feb',
    month_short_mar: 'Mar',
    month_short_apr: 'Apr',
    month_short_may: 'May',
    month_short_jun: 'Jun',
    month_short_jul: 'Jul',
    month_short_aug: 'Aug',
    month_short_sep: 'Sep',
    month_short_oct: 'Oct',
    month_short_nov: 'Nov',
    month_short_dec: 'Dec',
    range_start_label: 'Start',
    range_end_label: 'End'
  };

  const DATE_PICKER_I18N = {
    ...DATE_PICKER_I18N_DEFAULTS,
    ...(window.RoComponentsLocale?.datePicker || {})
  };

  /**
   * 日期选择器组件模板
   */
  const template = `
    <div class="date-picker__field" role="button" tabindex="0">
      <div class="date-picker__content">
        <div class="date-picker__inner">
          <span class="date-picker__label"></span>
          <span class="date-picker__value date-picker__value--placeholder"></span>
        </div>
      </div>
      <button type="button" class="date-picker__clear-btn" aria-label="${DATE_PICKER_I18N.clear_aria_label}">
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 3L13 13M13 3L3 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
      <svg class="date-picker__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/>
        <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" stroke-width="1.5"/>
        <line x1="9" y1="4" x2="9" y2="2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <line x1="15" y1="4" x2="15" y2="2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <circle cx="7" cy="13" r="1" fill="currentColor"/>
        <circle cx="12" cy="13" r="1" fill="currentColor"/>
        <circle cx="17" cy="13" r="1" fill="currentColor"/>
        <circle cx="7" cy="17" r="1" fill="currentColor"/>
        <circle cx="12" cy="17" r="1" fill="currentColor"/>
        <circle cx="17" cy="17" r="1" fill="currentColor"/>
      </svg>
    </div>

    <!-- PC端下拉日历 -->
    <div class="date-picker__dropdown">
      <div class="date-picker__calendar-view">
        <div class="date-picker__header">
          <button type="button" class="date-picker__nav-btn date-picker__nav-btn--prev" aria-label="${DATE_PICKER_I18N.prev_month_aria_label}">
            ${ICON_ARROW_LEFT}
          </button>
          <div class="date-picker__current">
            <span class="date-picker__month-year" role="button" tabindex="0"></span>
          </div>
          <button type="button" class="date-picker__nav-btn date-picker__nav-btn--next" aria-label="${DATE_PICKER_I18N.next_month_aria_label}">
            ${ICON_ARROW_RIGHT}
          </button>
        </div>
        <div class="date-picker__body">
          <div class="date-picker__weekdays">
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_sun}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_mon}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_tue}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_wed}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_thu}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_fri}</span>
            <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_sat}</span>
          </div>
          <div class="date-picker__days"></div>
        </div>
        <div class="date-picker__range-footer">
          <span><span class="date-picker__range-label">Start</span> <span class="date-picker__range-start"></span></span>
          <span class="date-picker__range-sep">-</span>
          <span><span class="date-picker__range-label">End</span> <span class="date-picker__range-end"></span></span>
        </div>
      </div>

      <div class="date-picker__selector date-picker__year-selector">
        <div class="date-picker__selector-header">
          <span class="date-picker__selector-title">${DATE_PICKER_I18N.year_selector_title}</span>
          <button type="button" class="date-picker__nav-btn date-picker__back-btn" aria-label="${DATE_PICKER_I18N.back_aria_label}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="date-picker__selector-grid date-picker__years-grid"></div>
      </div>

      <div class="date-picker__selector date-picker__month-selector">
        <div class="date-picker__selector-header">
          <span class="date-picker__selector-title">${DATE_PICKER_I18N.month_selector_title}</span>
          <button type="button" class="date-picker__nav-btn date-picker__back-btn" aria-label="${DATE_PICKER_I18N.back_aria_label}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="date-picker__selector-grid date-picker__months-grid"></div>
      </div>
    </div>

    <div class="date-picker__error-message"></div>

    <!-- 移动端弹窗 -->
    <div class="date-picker__modal">
      <div class="date-picker__modal-content">
        <div class="date-picker__modal-body">
          <div class="date-picker__mobile-calendar">
            <div class="date-picker__header">
              <button type="button" class="date-picker__nav-btn date-picker__nav-btn--prev" aria-label="${DATE_PICKER_I18N.prev_month_aria_label}">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <div class="date-picker__current">
                <span class="date-picker__month-year" role="button" tabindex="0"></span>
              </div>
              <button type="button" class="date-picker__nav-btn date-picker__nav-btn--next" aria-label="${DATE_PICKER_I18N.next_month_aria_label}">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            <div class="date-picker__body">
              <div class="date-picker__weekdays">
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_sun}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_mon}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_tue}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_wed}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_thu}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_fri}</span>
                <span class="date-picker__weekday">${DATE_PICKER_I18N.weekday_sat}</span>
              </div>
              <div class="date-picker__days"></div>
            </div>
            <div class="date-picker__range-footer">
              <span><span class="date-picker__range-label">Start</span> <span class="date-picker__range-start"></span></span>
              <span class="date-picker__range-sep">-</span>
              <span><span class="date-picker__range-label">End</span> <span class="date-picker__range-end"></span></span>
            </div>
          </div>

          <div class="date-picker__selector date-picker__year-selector">
            <div class="date-picker__selector-header">
              <span class="date-picker__selector-title">${DATE_PICKER_I18N.year_selector_title}</span>
              <button type="button" class="date-picker__nav-btn date-picker__back-btn" aria-label="${DATE_PICKER_I18N.back_aria_label}">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <div class="date-picker__selector-grid date-picker__years-grid"></div>
          </div>

          <div class="date-picker__selector date-picker__month-selector">
            <div class="date-picker__selector-header">
              <span class="date-picker__selector-title">${DATE_PICKER_I18N.month_selector_title}</span>
              <button type="button" class="date-picker__nav-btn date-picker__back-btn" aria-label="${DATE_PICKER_I18N.back_aria_label}">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <div class="date-picker__selector-grid date-picker__months-grid"></div>
          </div>
        </div>
        <div class="date-picker__modal-footer">
          <button type="button" class="date-picker__cancel-btn">${DATE_PICKER_I18N.cancel_button}</button>
          <button type="button" class="date-picker__confirm-btn">${DATE_PICKER_I18N.confirm_button}</button>
        </div>
      </div>
    </div>
  `;

  /**
   * 日期选择器 Web Component
   */
  class DatePickerElement extends HTMLElement {
    static get observedAttributes() {
      return [
        'value',
        'label',
        'placeholder',
        'min-date',
        'max-date',
        'name',
        'required',
        'disabled',
        'mode',
        'floating-label',
        'size'
      ];
    }

    constructor() {
      super();
      
      // 创建 Shadow DOM
      this.attachShadow({ mode: 'open' });
      
      // 添加样式
      const styleSheet = document.createElement('style');
      styleSheet.textContent = styles;
      this.shadowRoot.appendChild(styleSheet);
      
      // 添加模板
      const wrapper = document.createElement('div');
      wrapper.innerHTML = template;
      while (wrapper.firstChild) {
        this.shadowRoot.appendChild(wrapper.firstChild);
      }

      // 内部状态
      this._value = null;
      this._tempValue = null;
      this._startValue = null;
      this._endValue = null;
      this._tempStartValue = null;
      this._tempEndValue = null;
      this._currentYear = new Date().getFullYear();
      this._currentMonth = new Date().getMonth();
      this._isMobile = window.innerWidth < 1184;
      this._handleDocumentClick = this._handleDocumentClick.bind(this);

      // 缓存元素
      this._cacheElements();
      
      // 绑定事件
      this._bindEvents();
    }

    connectedCallback() {
      const sizeAttr = this.getAttribute('size');
      if (sizeAttr === 'normal') {
        this.setAttribute('size', 'medium');
      } else if (!this.hasAttribute('size')) {
        this.setAttribute('size', 'medium');
      }

      // 注册到管理器
      if (this.id) {
        DatePickerManager.register(this.id, this);
      }

      this._syncDisabledState();
      document.addEventListener('click', this._handleDocumentClick, true);

      // 初始化显示
      this._updateDisplay();
      
      // 初始化值
      if (this.getAttribute('value')) {
        this.value = this.getAttribute('value');
      }
    }

    disconnectedCallback() {
      // 从管理器注销
      if (this.id) {
        DatePickerManager.unregister(this.id);
      }
      document.removeEventListener('click', this._handleDocumentClick, true);
      if (this._dropdown.classList.contains('date-picker__dropdown--open')) {
        this._closeDropdown();
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;

      switch (name) {
        case 'value':
          this._setValue(newValue);
          break;
        case 'label': {
          const labelText = newValue || DATE_PICKER_I18N.label_default;
          this._labelEl.textContent = labelText;
          break;
        }
        case 'placeholder':
          // placeholder 不再展示，由 label 承担默认提示文本
          break;
        case 'disabled':
          this._syncDisabledState();
          break;
        case 'floating-label':
          /* 展示逻辑由 Shadow 内 :host([floating-label="false"]) 样式切换，无需额外脚本 */
          break;
        case 'size':
          /* 高度与字号由 :host([size=...]) 与 .date-picker__content--has-value 样式控制 */
          break;
        case 'mode':
          if (this.getAttribute('value')) this.value = this.getAttribute('value');
          break;
      }
    }

    // ==================== 公共属性 ====================

    get value() {
      if (this._isRangeMode()) {
        if (this._startValue && this._endValue) return this._startValue + ',' + this._endValue;
        if (this._startValue) return this._startValue;
        return null;
      }
      return this._value;
    }

    set value(val) {
      this._setValue(val);
    }

    get label() {
      return this.getAttribute('label') || DATE_PICKER_I18N.label_default;
    }

    set label(val) {
      this.setAttribute('label', val);
    }

    get name() {
      return this.getAttribute('name') || '';
    }

    set name(val) {
      this.setAttribute('name', val);
    }

    get minDate() {
      return this._parseDate(this.getAttribute('min-date'));
    }

    get maxDate() {
      return this._parseDate(this.getAttribute('max-date'));
    }

    get required() {
      return this.hasAttribute('required');
    }

    set required(val) {
      if (val) {
        this.setAttribute('required', '');
      } else {
        this.removeAttribute('required');
      }
    }

    // ==================== 私有方法 ====================

    _cacheElements() {
      this._field = this.shadowRoot.querySelector('.date-picker__field');
      this._content = this.shadowRoot.querySelector('.date-picker__content');
      this._labelEl = this.shadowRoot.querySelector('.date-picker__label');
      this._valueEl = this.shadowRoot.querySelector('.date-picker__value');
      this._clearBtn = this.shadowRoot.querySelector('.date-picker__clear-btn');
      this._errorEl = this.shadowRoot.querySelector('.date-picker__error-message');
      this._dropdown = this.shadowRoot.querySelector('.date-picker__dropdown');
      this._modal = this.shadowRoot.querySelector('.date-picker__modal');

      // PC端元素
      this._pcCalendarView = this._dropdown.querySelector('.date-picker__calendar-view');
      this._pcMonthYear = this._dropdown.querySelector('.date-picker__month-year');
      this._pcDaysGrid = this._dropdown.querySelector('.date-picker__days');
      this._pcYearSelector = this._dropdown.querySelector('.date-picker__year-selector');
      this._pcMonthSelector = this._dropdown.querySelector('.date-picker__month-selector');
      this._pcYearsGrid = this._dropdown.querySelector('.date-picker__years-grid');
      this._pcMonthsGrid = this._dropdown.querySelector('.date-picker__months-grid');

      // 移动端元素
      this._mobileCalendar = this._modal.querySelector('.date-picker__mobile-calendar');
      this._mobileMonthYear = this._modal.querySelector('.date-picker__month-year');
      this._mobileDaysGrid = this._modal.querySelector('.date-picker__days');
      this._mobileYearSelector = this._modal.querySelector('.date-picker__year-selector');
      this._mobileMonthSelector = this._modal.querySelector('.date-picker__month-selector');
      this._mobileYearsGrid = this._modal.querySelector('.date-picker__years-grid');
      this._mobileMonthsGrid = this._modal.querySelector('.date-picker__months-grid');
      this._pcRangeFooter = this._pcCalendarView.querySelector('.date-picker__range-footer');
      this._mobileRangeFooter = this._mobileCalendar.querySelector('.date-picker__range-footer');
    }

    _isRangeMode() {
      return this.getAttribute('mode') === 'range';
    }

    _bindEvents() {
      // 点击清除按钮
      this._clearBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._setValue(null);
      });

      // 点击输入框
      this._field.addEventListener('click', () => this._open());
      this._field.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._open();
        }
      });

      // PC端导航
      this._dropdown.querySelectorAll('.date-picker__nav-btn--prev').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._navigate(-1);
        });
      });
      this._dropdown.querySelectorAll('.date-picker__nav-btn--next').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._navigate(1);
        });
      });

      // PC端年月选择
      this._pcMonthYear.addEventListener('click', (e) => {
        e.stopPropagation();
        this._showYearSelector('pc');
      });
      this._pcYearSelector.querySelector('.date-picker__back-btn').addEventListener('click', (e) => {
        e.stopPropagation();
        this._showCalendar('pc');
      });
      this._pcMonthSelector.querySelector('.date-picker__back-btn').addEventListener('click', (e) => {
        e.stopPropagation();
        this._showYearSelector('pc');
      });

      // 移动端导航
      this._modal.querySelectorAll('.date-picker__nav-btn--prev').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._navigate(-1);
        });
      });
      this._modal.querySelectorAll('.date-picker__nav-btn--next').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._navigate(1);
        });
      });

      // 移动端年月选择
      this._mobileMonthYear.addEventListener('click', (e) => {
        e.stopPropagation();
        this._showYearSelector('mobile');
      });
      this._mobileYearSelector.querySelector('.date-picker__back-btn').addEventListener('click', (e) => {
        e.stopPropagation();
        this._showCalendar('mobile');
      });
      this._mobileMonthSelector.querySelector('.date-picker__back-btn').addEventListener('click', (e) => {
        e.stopPropagation();
        this._showYearSelector('mobile');
      });

      // 移动端：点遮罩（event.target 为遮罩层本身）关闭、取消关闭、确认提交
      this._modal.addEventListener('click', (e) => {
        if (e.target === this._modal) {
          this._closeModal();
        }
      });
      this._modal.querySelector('.date-picker__cancel-btn').addEventListener('click', () => this._closeModal());
      this._modal.querySelector('.date-picker__confirm-btn').addEventListener('click', () => this._confirmMobile());

      // 窗口大小改变
      window.addEventListener('resize', () => {
        this._isMobile = window.innerWidth < 1184;
      });
    }

    _handleDocumentClick(e) {
      if (this._isMobile || !this._dropdown.classList.contains('date-picker__dropdown--open')) return;

      const path = typeof e.composedPath === 'function' ? e.composedPath() : null;
      const isInsidePicker = path ? path.includes(this) : this.contains(e.target);
      if (!isInsidePicker) {
        this._closeDropdown();
      }
    }

    _syncDisabledState() {
      const dis = this.hasAttribute('disabled');
      this._field.classList.toggle('date-picker__field--disabled', dis);
      this._field.style.pointerEvents = '';
      this._field.style.opacity = '';
      this._field.setAttribute('tabindex', dis ? '-1' : '0');
    }

    _updateDisplay() {
      const labelText = this.getAttribute('label') || DATE_PICKER_I18N.label_default;
      this._labelEl.textContent = labelText;

      let displayVal = '';
      if (this._isRangeMode()) {
        if (this._startValue && this._endValue) {
          displayVal = this._startValue + RANGE_INPUT_DISPLAY_SEPARATOR + this._endValue;
        } else if (this._startValue) {
          displayVal = this._startValue;
        }
      } else {
        displayVal = this._value || '';
      }
      if (displayVal) {
        this._valueEl.textContent = displayVal;
        this._content.classList.add('date-picker__content--has-value');
        this._field.classList.add('date-picker__field--has-value');
      } else {
        this._valueEl.textContent = '';
        this._content.classList.remove('date-picker__content--has-value');
        this._field.classList.remove('date-picker__field--has-value');
      }
    }

    _parseDate(dateStr) {
      if (!dateStr) return null;
      if (dateStr === 'today') return new Date();
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    }

    _formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    _formatDateDisplay(dateStr) {
      if (!dateStr) return '';
      return dateStr.replace(/-/g, '/');
    }

    _updateRangeFooter() {
      if (!this._isRangeMode() || !this._pcRangeFooter || !this._mobileRangeFooter) return;
      const show = this._startValue || this._endValue;
      this._pcRangeFooter.classList.toggle('date-picker__range-footer--visible', !!show);
      this._mobileRangeFooter.classList.toggle('date-picker__range-footer--visible', !!show);
      if (show) {
        const startEls = this.shadowRoot.querySelectorAll('.date-picker__range-start');
        const endEls = this.shadowRoot.querySelectorAll('.date-picker__range-end');
        const labelEls = this.shadowRoot.querySelectorAll('.date-picker__range-label');
        startEls.forEach(el => { el.textContent = this._formatDateDisplay(this._getCurrentStart()); });
        endEls.forEach(el => { el.textContent = this._formatDateDisplay(this._getCurrentEnd()); });
        labelEls.forEach((el, i) => {
          el.textContent = i % 2 === 0 ? DATE_PICKER_I18N.range_start_label : DATE_PICKER_I18N.range_end_label;
        });
      }
    }

    _getCurrentStart() {
      return this._isMobile ? (this._tempStartValue || this._startValue) : this._startValue;
    }

    _getCurrentEnd() {
      return this._isMobile ? (this._tempEndValue || this._endValue) : this._endValue;
    }

    _setValue(val) {
      if (!val) {
        this._value = null;
        this._startValue = null;
        this._endValue = null;
        this._updateDisplay();
        this._updateRangeFooter();
        this.dispatchEvent(new CustomEvent('change', {
          bubbles: true,
          composed: true,
          detail: this._isRangeMode()
            ? { id: this.id, start: null, end: null, startDate: null, endDate: null }
            : { id: this.id, value: null, date: null }
        }));
        return;
      }

      if (this._isRangeMode()) {
        const parts = String(val).split(',');
        const startStr = parts[0] ? parts[0].trim() : null;
        const endStr = parts[1] ? parts[1].trim() : null;
        if (!startStr) return;
        const startDate = new Date(startStr);
        if (isNaN(startDate.getTime())) return;
        this._startValue = this._formatDate(startDate);
        if (endStr) {
          const endDate = new Date(endStr);
          if (!isNaN(endDate.getTime())) {
            const endFormatted = this._formatDate(endDate);
            if (endFormatted < this._startValue) {
              this._endValue = this._startValue;
              this._startValue = endFormatted;
            } else {
              this._endValue = endFormatted;
            }
          } else {
            this._endValue = null;
          }
        } else {
          this._endValue = null;
        }
        this._currentYear = startDate.getFullYear();
        this._currentMonth = startDate.getMonth();
        this._updateDisplay();
        this._updateRangeFooter();
        this._setError(false, '');
        this.dispatchEvent(new CustomEvent('change', {
          bubbles: true,
          composed: true,
          detail: {
            id: this.id,
            start: this._startValue,
            end: this._endValue,
            startDate: new Date(this._startValue),
            endDate: this._endValue ? new Date(this._endValue) : null
          }
        }));
        return;
      }

      const date = new Date(val);
      if (isNaN(date.getTime())) return;
      this._value = this._formatDate(date);
      this._currentYear = date.getFullYear();
      this._currentMonth = date.getMonth();
      this._updateDisplay();
      this._setError(false, '');
      this.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          id: this.id,
          value: this._value,
          date: date
        }
      }));
    }

    _open() {
      if (this.hasAttribute('disabled')) return;
      
      this._isMobile = window.innerWidth < 1184;
      
      if (this._isMobile) {
        if (this._isRangeMode()) {
          this._tempStartValue = this._startValue;
          this._tempEndValue = this._endValue;
        } else {
          this._tempValue = this._value;
        }
        this._openModal();
      } else {
        this._openDropdown();
      }
    }

    _applyDefaultOpenDateForSingleMode() {
      if (this._isRangeMode()) return;
      if (this._value) return;
      const attr = this.getAttribute('data-default-open-date');
      if (!attr) return;
      const d = this._parseDate(attr);
      if (!d) return;
      this._currentYear = d.getFullYear();
      this._currentMonth = d.getMonth();
    }

    _openDropdown() {
      this._applyDefaultOpenDateForSingleMode();
      this._positionDropdown();
      this._dropdown.classList.add('date-picker__dropdown--open');
      this._field.classList.add('date-picker__field--focus');
      this._showCalendar('pc');
      this._renderCalendar('pc');
      this._updateRangeFooter();

      // 滚动或窗口尺寸变化时关闭下拉，避免位置错位
      this._dropdownScrollHandler = () => this._closeDropdown();
      this._dropdownResizeHandler = () => this._closeDropdown();
      window.addEventListener('scroll', this._dropdownScrollHandler, true);
      window.addEventListener('resize', this._dropdownResizeHandler);
    }

    _closeDropdown() {
      if (this._isRangeMode() && this._startValue && !this._endValue) {
        this._startValue = null;
        this._updateDisplay();
      }
      this._dropdown.classList.remove('date-picker__dropdown--open');
      this._field.classList.remove('date-picker__field--focus');

      if (this._dropdownScrollHandler) {
        window.removeEventListener('scroll', this._dropdownScrollHandler, true);
        this._dropdownScrollHandler = null;
      }
      if (this._dropdownResizeHandler) {
        window.removeEventListener('resize', this._dropdownResizeHandler);
        this._dropdownResizeHandler = null;
      }

      this.validate();
    }

    /**
     * 视口可用宽度（PC 下拉用 fixed，优先 visualViewport 应对部分布局）
     */
    _getPcViewportWidth() {
      const vv = window.visualViewport;
      return vv && vv.width > 0 ? vv.width : window.innerWidth;
    }

    /**
     * 计算 PC 下拉面板的合法水平区间 [clipLeft, clipRight]
     * - 基准为视口左右各留 margin
     * - 仅与 overflow-x 为 hidden / clip 的祖先求交（不把 auto/scroll 当裁剪区，避免整页滚动容器导致 clip 过宽、右对齐永远不触发）
     */
    _getPcDropdownHorizontalClip() {
      const margin = 8;
      const vw = this._getPcViewportWidth();
      let clipLeft = margin;
      let clipRight = vw - margin;

      let el = this.parentElement;
      const maxDepth = 24;
      let depth = 0;
      while (el && el !== document.documentElement && depth < maxDepth) {
        const ox = window.getComputedStyle(el).overflowX;
        if (ox === 'hidden' || ox === 'clip') {
          const r = el.getBoundingClientRect();
          clipLeft = Math.max(clipLeft, r.left + margin);
          clipRight = Math.min(clipRight, r.right - margin);
        }
        el = el.parentElement;
        depth++;
      }

      if (clipRight - clipLeft < PC_DROPDOWN_PANEL_WIDTH * 0.5) {
        clipLeft = margin;
        clipRight = vw - margin;
      }

      return { clipLeft, clipRight };
    }

    /**
     * 左对齐时面板右缘是否会越过「视口安全右边界」（与是否触发右对齐强相关）
     */
    _pcFieldLeftAlignWouldOverflowViewport(fieldRect, panelWidth) {
      const margin = 8;
      const vpRight = this._getPcViewportWidth() - margin;
      return fieldRect.left + panelWidth > vpRight - 0.5;
    }

    /**
     * 窄输入（panel 比 input 宽）：若 input 中心落在安全区 [clipLeft, clipRight] 的**右半区**（严格大于中点），
     * 视为布局上「靠右」（flex-end、侧栏右列、max-width 容器内贴右等）。
     * 此时仅用「左对齐 + panel 320px」不会挤爆视口，但面板会明显伸出 input 右缘；改为与 input 右缘对齐更合理。
     */
    _pcNarrowFieldInRightHalfOfClip(fieldRect, clipLeft, clipRight) {
      const midField = fieldRect.left + fieldRect.width / 2;
      const midClip = (clipLeft + clipRight) / 2;
      return midField > midClip;
    }

    /**
     * PC 端下拉水平位置：
     * - input 宽度 > 面板宽度 → 先相对 input 居中；若仍会越过 clip 右边界，则改为面板右缘贴 input 右缘
     * - 否则默认左对齐；若视口溢出、clip 溢出，或窄输入落在安全区右半区 → 右对齐（面板右缘 = input 右缘）
     * - 最后在 [clipLeft, clipRight - panelWidth] 内夹紧
     */
    _computePcDropdownLeft(fieldRect, panelWidth) {
      const { clipLeft, clipRight } = this._getPcDropdownHorizontalClip();
      let left;

      if (fieldRect.width > panelWidth) {
        left = fieldRect.left + (fieldRect.width - panelWidth) / 2;
        if (left + panelWidth > clipRight - 0.5) {
          left = fieldRect.right - panelWidth;
        }
      } else {
        left = fieldRect.left;
        if (
          this._pcFieldLeftAlignWouldOverflowViewport(fieldRect, panelWidth) ||
          left + panelWidth > clipRight - 0.5 ||
          this._pcNarrowFieldInRightHalfOfClip(fieldRect, clipLeft, clipRight)
        ) {
          left = fieldRect.right - panelWidth;
        }
      }

      const maxLeft = clipRight - panelWidth;
      return Math.max(clipLeft, Math.min(left, maxLeft));
    }

    _positionDropdown() {
      const rect = this._field.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = this._isRangeMode() ? 400 : 340;

      const panelWidth =
        this._dropdown.offsetWidth > 0 ? this._dropdown.offsetWidth : PC_DROPDOWN_PANEL_WIDTH;
      const leftPx = this._computePcDropdownLeft(rect, panelWidth);
      this._dropdown.style.left = leftPx + 'px';

      if (rect.bottom + 8 + dropdownHeight <= viewportHeight) {
        this._dropdown.style.top = (rect.bottom + 8) + 'px';
        this._dropdown.style.bottom = 'auto';
      } else {
        this._dropdown.style.top = 'auto';
        this._dropdown.style.bottom = (viewportHeight - rect.top + 8) + 'px';
      }
    }

    _openModal() {
      this._applyDefaultOpenDateForSingleMode();
      this._modal.classList.add('date-picker__modal--open');
      document.body.style.overflow = 'hidden';
      this._showCalendar('mobile');
      this._renderCalendar('mobile');
      this._updateRangeFooter();
    }

    _closeModal() {
      this._modal.classList.remove('date-picker__modal--open');
      document.body.style.overflow = '';
      this._tempValue = null;
      this._tempStartValue = null;
      this._tempEndValue = null;
      this.validate();
    }

    _confirmMobile() {
      if (this._isRangeMode()) {
        if (this._tempStartValue && this._tempEndValue) {
          this._setValue(this._tempStartValue + ',' + this._tempEndValue);
        } else if (this._tempStartValue) {
          this._setValue(this._tempStartValue);
        }
      } else if (this._tempValue) {
        this._setValue(this._tempValue);
      }
      this._closeModal();
    }

    _showCalendar(target) {
      if (target === 'pc') {
        this._pcCalendarView.style.display = 'block';
        this._pcYearSelector.classList.remove('date-picker__selector--open');
        this._pcMonthSelector.classList.remove('date-picker__selector--open');
      } else {
        this._mobileCalendar.style.display = 'block';
        this._mobileYearSelector.classList.remove('date-picker__selector--open');
        this._mobileMonthSelector.classList.remove('date-picker__selector--open');
      }
    }

    _showYearSelector(target) {
      if (target === 'pc') {
        this._pcCalendarView.style.display = 'none';
        this._pcYearSelector.classList.add('date-picker__selector--open');
        this._pcMonthSelector.classList.remove('date-picker__selector--open');
        this._renderYears(this._pcYearsGrid, target);
      } else {
        this._mobileCalendar.style.display = 'none';
        this._mobileYearSelector.classList.add('date-picker__selector--open');
        this._mobileMonthSelector.classList.remove('date-picker__selector--open');
        this._renderYears(this._mobileYearsGrid, target);
      }
    }

    _showMonthSelector(target) {
      if (target === 'pc') {
        this._pcYearSelector.classList.remove('date-picker__selector--open');
        this._pcMonthSelector.classList.add('date-picker__selector--open');
        this._renderMonths(this._pcMonthsGrid, target);
      } else {
        this._mobileYearSelector.classList.remove('date-picker__selector--open');
        this._mobileMonthSelector.classList.add('date-picker__selector--open');
        this._renderMonths(this._mobileMonthsGrid, target);
      }
    }

    _navigate(direction) {
      this._currentMonth += direction;
      
      if (this._currentMonth < 0) {
        this._currentMonth = 11;
        this._currentYear--;
      } else if (this._currentMonth > 11) {
        this._currentMonth = 0;
        this._currentYear++;
      }

      this._renderCalendar(this._isMobile ? 'mobile' : 'pc');
    }

    _renderCalendar(target) {
      const monthYear = target === 'pc' ? this._pcMonthYear : this._mobileMonthYear;
      const daysGrid = target === 'pc' ? this._pcDaysGrid : this._mobileDaysGrid;

      const monthNames = [
        DATE_PICKER_I18N.month_january,
        DATE_PICKER_I18N.month_february,
        DATE_PICKER_I18N.month_march,
        DATE_PICKER_I18N.month_april,
        DATE_PICKER_I18N.month_may,
        DATE_PICKER_I18N.month_june,
        DATE_PICKER_I18N.month_july,
        DATE_PICKER_I18N.month_august,
        DATE_PICKER_I18N.month_september,
        DATE_PICKER_I18N.month_october,
        DATE_PICKER_I18N.month_november,
        DATE_PICKER_I18N.month_december
      ];
      monthYear.textContent = `${monthNames[this._currentMonth]} ${this._currentYear}`;

      daysGrid.innerHTML = '';
      daysGrid.classList.remove('date-picker__days--range');

      const firstDay = new Date(this._currentYear, this._currentMonth, 1);
      const lastDay = new Date(this._currentYear, this._currentMonth + 1, 0);
      const startDayOfWeek = firstDay.getDay();
      const daysInMonth = lastDay.getDate();
      const today = new Date();
      const currentValue = this._isMobile ? this._tempValue : this._value;
      const rangeStart = this._isMobile ? this._tempStartValue : this._startValue;
      const rangeEnd = this._isMobile ? this._tempEndValue : this._endValue;
      const useRangeBars = this._isRangeMode() && rangeStart && rangeEnd;

      const cells = [];
      const prevMonthLastDay = new Date(this._currentYear, this._currentMonth, 0).getDate();
      for (let i = startDayOfWeek - 1; i >= 0; i--) {
        const d = prevMonthLastDay - i;
        const date = new Date(this._currentYear, this._currentMonth - 1, d);
        const dateStr = this._formatDate(date);
        let rangeState = null;
        if (useRangeBars && dateStr >= rangeStart && dateStr <= rangeEnd) {
          const isStart = dateStr === rangeStart;
          const isEnd = dateStr === rangeEnd;
          rangeState = { isStart, isEnd, isInRange: true };
        }
        cells.push({ day: d, isOtherMonth: true, monthOffset: -1, date, dateStr, rangeState });
      }
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(this._currentYear, this._currentMonth, day);
        const dateStr = this._formatDate(date);
        const isToday = date.toDateString() === today.toDateString();
        let isSelected = currentValue === dateStr;
        let rangeState = null;
        if (useRangeBars) {
          const isStart = dateStr === rangeStart;
          const isEnd = dateStr === rangeEnd;
          const isInRange = dateStr >= rangeStart && dateStr <= rangeEnd;
          isSelected = isStart || isEnd;
          rangeState = { isStart, isEnd, isInRange };
        } else if (this._isRangeMode() && rangeStart) {
          isSelected = dateStr === rangeStart;
          rangeState = { isStart: isSelected, isEnd: false, isInRange: false };
        }
        const isDisabled = this._isDateDisabled(date);
        cells.push({ day, isOtherMonth: false, monthOffset: 0, isToday, isSelected, isDisabled, date, dateStr, rangeState });
      }
      const totalCells = Math.ceil((startDayOfWeek + daysInMonth) / 7) * 7;
      const remainingCells = totalCells - startDayOfWeek - daysInMonth;
      for (let i = 1; i <= remainingCells; i++) {
        const date = new Date(this._currentYear, this._currentMonth + 1, i);
        const dateStr = this._formatDate(date);
        let rangeState = null;
        if (useRangeBars && dateStr >= rangeStart && dateStr <= rangeEnd) {
          const isStart = dateStr === rangeStart;
          const isEnd = dateStr === rangeEnd;
          rangeState = { isStart, isEnd, isInRange: true };
        }
        cells.push({ day: i, isOtherMonth: true, monthOffset: 1, date, dateStr, rangeState });
      }

      if (useRangeBars) {
        daysGrid.classList.add('date-picker__days--range');
        for (let r = 0; r < cells.length; r += 7) {
          const rowCells = cells.slice(r, r + 7);
          const weekRow = document.createElement('div');
          weekRow.className = 'date-picker__week-row';
          let minCol = -1, maxCol = -1;
          rowCells.forEach((c, col) => {
            if (c.rangeState && (c.rangeState.isInRange || c.rangeState.isStart || c.rangeState.isEnd)) {
              if (minCol < 0) minCol = col;
              maxCol = col;
            }
          });
          if (minCol >= 0 && maxCol >= 0) {
            const bar = document.createElement('div');
            bar.className = 'date-picker__range-bar';
            const cellW = 'calc((100% - 24px) / 7)';
            bar.style.left = `calc(${cellW} * ${minCol} + 4px * ${minCol})`;
            bar.style.width = `calc(${cellW} * ${maxCol - minCol + 1} + 4px * ${maxCol - minCol})`;
            weekRow.appendChild(bar);
          }
          rowCells.forEach(c => {
            const dayBtn = this._createDayButton(
              c.day,
              c.isOtherMonth,
              c.monthOffset,
              c.isToday || false,
              c.isSelected || false,
              c.isDisabled || false,
              c.date,
              c.rangeState
            );
            weekRow.appendChild(dayBtn);
          });
          daysGrid.appendChild(weekRow);
        }
      } else {
        cells.forEach(c => {
          const dayBtn = this._createDayButton(
            c.day,
            c.isOtherMonth,
            c.monthOffset || 0,
            c.isToday || false,
            c.isSelected || false,
            c.isDisabled || false,
            c.date,
            c.rangeState
          );
          daysGrid.appendChild(dayBtn);
        });
      }

      this._updateNavButtons(target);
      if (this._isRangeMode()) this._updateRangeFooter();
    }

    _createDayButton(day, isOtherMonth, monthOffset = 0, isToday = false, isSelected = false, isDisabled = false, date = null, rangeState = null) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'date-picker__day';
      btn.textContent = day;

      if (isOtherMonth) btn.classList.add('date-picker__day--other-month');
      if (isToday) btn.classList.add('date-picker__day--today');
      if (isSelected) btn.classList.add('date-picker__day--selected');
      if (isDisabled) btn.disabled = true;
      if (rangeState) {
        if (rangeState.isInRange) btn.classList.add('date-picker__day--in-range');
        if (rangeState.isStart) btn.classList.add('date-picker__day--range-start');
        if (rangeState.isEnd) btn.classList.add('date-picker__day--range-end');
      }

      if (!isOtherMonth && !isDisabled && date) {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const dateStr = this._formatDate(date);
          if (this._isRangeMode()) {
            const start = this._isMobile ? this._tempStartValue : this._startValue;
            const end = this._isMobile ? this._tempEndValue : this._endValue;
            if (!start || (start && end)) {
              if (this._isMobile) {
                this._tempStartValue = dateStr;
                this._tempEndValue = null;
              } else {
                this._startValue = dateStr;
                this._endValue = null;
                this._updateDisplay();
              }
              this._renderCalendar(this._isMobile ? 'mobile' : 'pc');
            } else {
              const newStart = dateStr < start ? dateStr : start;
              const newEnd = dateStr < start ? start : dateStr;
              if (this._isMobile) {
                this._tempStartValue = newStart;
                this._tempEndValue = newEnd;
                this._renderCalendar('mobile');
              } else {
                this._setValue(newStart + ',' + newEnd);
                this._closeDropdown();
              }
            }
          } else {
            if (this._isMobile) {
              this._tempValue = dateStr;
              this._renderCalendar('mobile');
            } else {
              this._setValue(dateStr);
              this._closeDropdown();
            }
          }
        });
      } else if (isOtherMonth) {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._currentMonth += monthOffset;
          if (this._currentMonth < 0) {
            this._currentMonth = 11;
            this._currentYear--;
          } else if (this._currentMonth > 11) {
            this._currentMonth = 0;
            this._currentYear++;
          }
          this._renderCalendar(this._isMobile ? 'mobile' : 'pc');
        });
      }

      return btn;
    }

    _isDateDisabled(date) {
      const minDate = this.minDate;
      const maxDate = this.maxDate;
      
      if (minDate && date < new Date(minDate.toDateString())) return true;
      if (maxDate && date > new Date(maxDate.toDateString())) return true;
      return false;
    }

    _updateNavButtons(target) {
      const container = target === 'pc' ? this._dropdown : this._modal;
      const prevBtns = container.querySelectorAll('.date-picker__nav-btn--prev');
      const nextBtns = container.querySelectorAll('.date-picker__nav-btn--next');

      const minDate = this.minDate;
      const maxDate = this.maxDate;

      if (minDate) {
        const prevMonth = new Date(this._currentYear, this._currentMonth, 0);
        const canGoPrev = prevMonth >= minDate;
        prevBtns.forEach(btn => btn.disabled = !canGoPrev);
      }

      if (maxDate) {
        const nextMonth = new Date(this._currentYear, this._currentMonth + 1, 1);
        const canGoNext = nextMonth <= maxDate;
        nextBtns.forEach(btn => btn.disabled = !canGoNext);
      }
    }

    _renderYears(container, target) {
      container.innerHTML = '';
      
      const currentYear = new Date().getFullYear();
      const minDate = this.minDate;
      const maxDate = this.maxDate;
      const startYear = minDate ? minDate.getFullYear() : currentYear - 100;
      const endYear = maxDate ? maxDate.getFullYear() : currentYear + 10;

      for (let year = endYear; year >= startYear; year--) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'date-picker__selector-item';
        btn.textContent = year;

        if (year === this._currentYear) {
          btn.classList.add('date-picker__selector-item--selected');
        }

        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._currentYear = year;
          this._showMonthSelector(target);
        });

        container.appendChild(btn);
      }
    }

    _renderMonths(container, target) {
      container.innerHTML = '';
      
      const monthNames = [
        DATE_PICKER_I18N.month_short_jan,
        DATE_PICKER_I18N.month_short_feb,
        DATE_PICKER_I18N.month_short_mar,
        DATE_PICKER_I18N.month_short_apr,
        DATE_PICKER_I18N.month_short_may,
        DATE_PICKER_I18N.month_short_jun,
        DATE_PICKER_I18N.month_short_jul,
        DATE_PICKER_I18N.month_short_aug,
        DATE_PICKER_I18N.month_short_sep,
        DATE_PICKER_I18N.month_short_oct,
        DATE_PICKER_I18N.month_short_nov,
        DATE_PICKER_I18N.month_short_dec
      ];
      const minDate = this.minDate;
      const maxDate = this.maxDate;

      for (let month = 0; month < 12; month++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'date-picker__selector-item';
        btn.textContent = monthNames[month];

        const firstDayOfMonth = new Date(this._currentYear, month, 1);
        const lastDayOfMonth = new Date(this._currentYear, month + 1, 0);
        
        let disabled = false;
        if (minDate && lastDayOfMonth < minDate) disabled = true;
        if (maxDate && firstDayOfMonth > maxDate) disabled = true;

        if (disabled) btn.disabled = true;
        if (month === this._currentMonth && !disabled) {
          btn.classList.add('date-picker__selector-item--selected');
        }

        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._currentMonth = month;
          this._showCalendar(target);
          this._renderCalendar(target);
        });

        container.appendChild(btn);
      }
    }

    // ==================== 校验 ====================

    validate() {
      if (this.hasAttribute('disabled')) return { valid: true, message: '' };
      const errorMessage = this._validateRequired() ?? '';
      const valid = !errorMessage;
      this._setError(!valid, errorMessage);
      return { valid, message: errorMessage };
    }

    clearError() {
      this._setError(false, '');
    }

    _validateRequired() {
      if (!this.hasAttribute('required')) return null;
      if (this._isRangeMode()) {
        if (this._startValue && this._endValue) return null;
      } else if (this._value) {
        return null;
      }
      return this.getAttribute('error-message') || window.RoFormLocale?.required || 'This field is required';
    }

    _setError(isError, message) {
      if (isError) {
        this._field.classList.add('date-picker__field--error');
        this._errorEl.textContent = message;
        this._errorEl.classList.add('date-picker__error-message--visible');
      } else {
        this._field.classList.remove('date-picker__field--error');
        this._errorEl.textContent = '';
        this._errorEl.classList.remove('date-picker__error-message--visible');
      }
    }

    // ==================== 表单集成 ====================

    // 支持表单提交
    get form() {
      return this.closest('form');
    }

    // 表单重置
    formResetCallback() {
      this.value = this.getAttribute('value') || null;
    }

    // 表单数据
    get formValue() {
      return this._isRangeMode() ? this.value : this._value;
    }
  }

  // 注册自定义元素
  customElements.define('ro-date-picker', DatePickerElement);

})();
